import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';
import { IMessageTemplate } from '@clickersphere/core/entities/IMessageTemplate';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import { MESSAGE_TEMPLATE_VARIABLES } from '@clickersphere/core/utils/message-template';
import { useCreateMessageTemplate } from '../../../../app/hooks/message-template/useCreateMessageTemplate';
import { useUpdateMessageTemplate } from '../../../../app/hooks/message-template/useUpdateMessageTemplate';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';

export function useMessageTemplateModalController(
  messageTemplate?: IMessageTemplate,
) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { handleError } = useError();
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const { handleCloseModal } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );

  const { createMessageTemplateAsync, createMessageTemplateIsPending } =
    useCreateMessageTemplate();
  const { updateMessageTemplateAsync, updateMessageTemplateIsPending } =
    useUpdateMessageTemplate();

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .trim()
          .nonempty(t('settings.messageTemplates.modal.errors.name.required'))
          .max(120, t('settings.messageTemplates.modal.errors.name.maxLength')),
        body: z
          .string()
          .trim()
          .nonempty(t('settings.messageTemplates.modal.errors.body.required'))
          .max(
            2000,
            t('settings.messageTemplates.modal.errors.body.maxLength'),
          ),
      }),
    [t],
  );

  type FormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit: hookFormHandleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: messageTemplate?.name ?? '',
      body: messageTemplate?.body ?? '',
    },
  });

  const { ref: bodyFieldRef, ...bodyField } = register('body');

  function assignBodyRef(element: HTMLTextAreaElement | null) {
    bodyRef.current = element;
    bodyFieldRef(element);
  }

  function handleInsertVariable(variable: string) {
    const token = `{{${variable}}}`;
    const textarea = bodyRef.current;
    const current = getValues('body') ?? '';

    if (!textarea) {
      setValue('body', `${current}${token}`, { shouldValidate: true });
      return;
    }

    const start = textarea.selectionStart ?? current.length;
    const end = textarea.selectionEnd ?? current.length;
    const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
    setValue('body', next, { shouldValidate: true });

    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + token.length;
      textarea.setSelectionRange(cursor, cursor);
    });
  }

  const handleSubmit = hookFormHandleSubmit(async (data) => {
    try {
      if (messageTemplate) {
        await updateMessageTemplateAsync({
          id: messageTemplate.id,
          data,
        });
        toast(t('settings.messageTemplates.modal.toast.updated'), 'success');
      } else {
        await createMessageTemplateAsync(data);
        toast(t('settings.messageTemplates.modal.toast.created'), 'success');
      }
      handleCloseModal();
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
        return;
      }
      toast(t('settings.messageTemplates.modal.toast.error'), 'error');
    }
  });

  return {
    t,
    register,
    errors,
    bodyField,
    assignBodyRef,
    variables: MESSAGE_TEMPLATE_VARIABLES,
    handleInsertVariable,
    handleSubmit,
    handleClose: handleCloseModal,
    isPending: createMessageTemplateIsPending || updateMessageTemplateIsPending,
  };
}
